import type { ICard } from "@/modules/card/common/types";
import { useGetCardsQuery } from "@/modules/card/services";
import { setShowBalances } from "@/modules/dashboard/state";
import { INITIAL_PAGE } from "@/shared/common/constants";
import { AppPaths } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { PageWidgetTemplate } from "@/shared/components";
import { useAppDispatch, useShowBalance } from "@/shared/hooks";
import { setActiveCardId } from "@/shared/state/common";
import classNames from "classnames";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { CardItem } from "./CardItem";

interface Props {
  limit?: number;
  cardId?: string;
}

export const Cards = ({
  limit = 1000,
  cardId,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { handleShowBalances } = useShowBalance();

  const {
    data: cardList,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetCardsQuery({
    pageIndex: INITIAL_PAGE,
    pageSize: limit,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const onShowDetails = (card: ICard) => {
    dispatch(setActiveCardId(card.id));
    if (!location.pathname.includes(AppPaths.Cards)) {
      navigate(AppPaths.Cards);
    }
  };

  useEffect(() => {
    if (cardList) {
      const showBalances = Array(cardList?.length).fill(false);
      dispatch(setShowBalances(showBalances));
    }
  }, [cardList]);

  return (
    <PageWidgetTemplate
      isFetching={isFetching}
      isError={isError}
      error={error as IError}
      refetch={refetch}
      isEmpty={!cardList?.length}
    >
      {cardList?.map((card, i) => (
        <div key={card.id} onClick={() => onShowDetails(card)}>
          <CardItem
            {...card}
            className={classNames({ "selected": cardId === `${card.id}` })}
            handleShowBalances={handleShowBalances}
            index={i}
          />
        </div>
      ))}
    </PageWidgetTemplate>
  );
};
